import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {R1C1Location, R1C1Range} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {
    ILearnExperienceActivePageData,
    ILearnExperienceActiveSheetData,
    ILearnExperienceAddComponentData,
    ILearnExperienceCellLocation,
    ILearnExperienceCellRange,
    ILearnExperienceCellStateData,
    ILearnExperienceComponentData,
    ILearnExperienceCopyPageFromData,
    ILearnExperienceCurrentContextData,
    ILearnExperienceData,
    ILearnExperienceFrameDockData,
    ILearnExperienceFrameFullOverlayData,
    ILearnExperienceGetBoundsData,
    ILearnExperienceMessage,
    ILearnExperienceMoveComponentData,
    ILearnExperienceSelectedCellData,
    ILearnExperienceSetComponentPositionData,
    ILearnExperienceSetFrameBoundsData,
    ILearnExperienceSetPositionData,
    ILearnExperienceSetSizeData,
    ILearnExperienceUiPartStateData,
    LEARN_EXPERIENCE_MESSAGE,
    LearnExperienceCellState,
    LearnExperienceFrameDockSide,
    LearnExperienceFunction,
    LearnExperienceUiPart
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/LearnExperienceState';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    IFormulaExpression,
    ISheetAPI,
    ISheetLocation,
    ISheetRange,
    SheetType,
    SheetValueType
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';

export class LearnExperienceFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'lx';
    }

    private _processLxFunc(data:ILearnExperienceData):ABoolean
    {
        if( !window.parent )
            return ABoolean.false();

        const message:ILearnExperienceMessage = {
            id: IdUtil.newGUID(),
            type: LEARN_EXPERIENCE_MESSAGE,
            data
        };

        window.parent.postMessage(message, '*');

        return ABoolean.true();
    }

    private async _processLxGetFunc<T extends ILearnExperienceData>(data:ILearnExperienceData):Promise<T>
    {
        if( !window.parent )
            throw new Error('#INVALID_STATE');

        const message:ILearnExperienceMessage = {
            id: IdUtil.newGUID(),
            type: LEARN_EXPERIENCE_MESSAGE,
            data
        };

        let onMessage:any = null;
        let timeoutTimer:any = null;

        const promise:Promise<T> = new Promise<T>((resolve, reject) =>
        {
            timeoutTimer = setTimeout(
                () => reject(new Error('#TIMEOUT')),
                5000
            );

            onMessage = (event:MessageEvent):void =>
            {
                if( event.source !== window && event.data && event.data.type === LEARN_EXPERIENCE_MESSAGE )
                {
                    const responseMessage:ILearnExperienceMessage = event.data as ILearnExperienceMessage;

                    if( responseMessage.id === message.id && responseMessage.data )
                        resolve(responseMessage.data as T);
                }
            };
        });

        window.addEventListener('message', onMessage, false);
        window.parent.postMessage(message, '*');

        try
        {
            const result:T = await promise;

            return result;
        }
        catch( error )
        {
            throw error;
        }
        finally
        {
            clearTimeout(timeoutTimer);
            window.removeEventListener('message', onMessage, false);
        }
    }

    @Function({
        title: 'LX_SET_FRAME_POSITION(horizontal_align, vertical_align)',
        tip: '',
        alias: 'LX_SET_FRAME_POSITION',
        example: 'LX_SET_FRAME_POSITION("center", -35)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetFramePosition(@Param({
                                  title: 'horizontal_align',
                                  tip: 'horizontal_align',
                                  type: SheetType.AnyIValue
                              }) horizontalAlign:IValue,
                              @Param({
                                  title: 'vertical_align',
                                  tip: 'vertical_align',
                                  type: SheetType.AnyIValue
                              }) verticalAlign:IValue):ABoolean
    {
        const data:ILearnExperienceSetPositionData = {
            lxFunction: LearnExperienceFunction.SET_POSITION,
            horizontalAlign: horizontalAlign.toString(),
            verticalAlign: verticalAlign.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_SET_FRAME_SIZE(width, height)',
        tip: '',
        alias: 'LX_SET_FRAME_SIZE',
        example: 'LX_SET__FRAME_SIZE(100, "30%")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetFrameSize(@Param({
                              title: 'width',
                              tip: 'width',
                              type: SheetType.AnyIValue
                          }) width:IValue,
                          @Param({
                              title: 'height',
                              tip: 'height',
                              type: SheetType.AnyIValue
                          }) height:IValue):ABoolean
    {
        const data:ILearnExperienceSetSizeData = {
            lxFunction: LearnExperienceFunction.SET_SIZE,
            width: width.toString(),
            height: height.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_FRAME_BOUNDS()',
        tip: '',
        alias: 'LX_GET_FRAME_BOUNDS',
        example: 'LX_GET_FRAME_BOUNDS()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Frame,
            }
    })
    @bind
    public async lxGetFrameBounds():Promise<IFrame | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_FRAME_BOUNDS
        };

        try
        {
            const responseData:ILearnExperienceGetBoundsData =
                await this._processLxGetFunc<ILearnExperienceGetBoundsData>(data);

            return Frames.fromFieldsAndData(
                [
                    new FrameField('x', Type.Number),
                    new FrameField('y', Type.Number),
                    new FrameField('width', Type.Number),
                    new FrameField('height', Type.Number)
                ],
                [
                    [
                        ANumber.fromNumber(responseData.x),
                        ANumber.fromNumber(responseData.y),
                        ANumber.fromNumber(responseData.width),
                        ANumber.fromNumber(responseData.height)
                    ]
                ]
            );
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_FRAME_BOUNDS(horizontal_align, vertical_align, width, height)',
        tip: '',
        alias: 'LX_SET_FRAME_BOUNDS',
        example: 'LX_SET_FRAME_BOUNDS(100, "30%")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetFrameBounds(@Param({
                                title: 'horizontal_align',
                                tip: 'horizontal_align',
                                type: SheetType.AnyIValue
                            }) horizontalAlign:IValue,
                            @Param({
                                title: 'vertical_align',
                                tip: 'vertical_align',
                                type: SheetType.AnyIValue
                            }) verticalAlign:IValue,
                            @Param({
                                title: 'width',
                                tip: 'width',
                                type: SheetType.AnyIValue
                            }) width:IValue,
                            @Param({
                                title: 'height',
                                tip: 'height',
                                type: SheetType.AnyIValue
                            }) height:IValue):ABoolean
    {
        const data:ILearnExperienceSetFrameBoundsData = {
            lxFunction: LearnExperienceFunction.SET_FRAME_BOUNDS,
            horizontalAlign: horizontalAlign.toString(),
            verticalAlign: verticalAlign.toString(),
            width: width.toString(),
            height: height.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_COPY_PAGE(space_name, design_name, is_import_related_sheets, prefix_for_pages_and_sheets, page_names...)',
        tip: '',
        alias: 'LX_COPY_PAGE',
        example: 'LX_COPY_PAGE("Space 1", "Design 1", TRUE, "Copy_of_", "Page 1", "Page 2", ...)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxCopyPage(@Param({
                                title: 'project_id',
                                tip: 'project_id',
                                type: Type.Integer
                            }) projectId:AInteger,
                            @Param({
                                title: 'design_id',
                                tip: 'design_id',
                                type: Type.Integer
                            }) designId:AInteger,
                            @Param({
                                title: 'is_import_related_sheets',
                                tip: 'is_import_related_sheets',
                                type: Type.Boolean
                            }) isImportRelatedSheets:ABoolean,
                            @Param({
                                title: 'prefix_for_pages_and_sheets',
                                tip: 'prefix_for_pages_and_sheets',
                                type: Type.String
                            }) prefixForPagesAndSheets:AString,
                            @Param({
                                title: 'page_names',
                                tip: 'Page names',
                                type: SheetType.FormulaExpression,
                                repeatable: true
                            }) pageNamesExpr:Array<IFormulaExpression>):Promise<ABoolean | AError>
    {
        const pageNames:Array<IValue> = await Promise.all(
            pageNamesExpr.map(
                (pageNamesExpr:IFormulaExpression) => pageNamesExpr.eval()
            )
        );

        const data:ILearnExperienceCopyPageFromData = {
            lxFunction: LearnExperienceFunction.COPY_PAGE_FROM,
            projectId: projectId.value,
            designId: designId.value,
            pageNames: pageNames.map((pageName:IValue) => pageName.toString()),
            isImportSheets: isImportRelatedSheets.value,
            importPrefix: prefixForPagesAndSheets.toString()
        };

        try
        {
            await this._processLxGetFunc<ILearnExperienceData>(data);

            return ABoolean.true();
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_GET_ACTIVE_PAGE()',
        tip: '',
        alias: 'LX_GET_ACTIVE_PAGE',
        example: 'LX_GET_ACTIVE_PAGE()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetActivePage():Promise<AString | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_PAGE
        };

        try
        {
            const responseData:ILearnExperienceActivePageData =
                await this._processLxGetFunc<ILearnExperienceActivePageData>(data);

            return AString.fromString(responseData.pageName);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_ACTIVE_PAGE(page_name)',
        tip: '',
        alias: 'LX_SET_ACTIVE_PAGE',
        example: 'LX_SET_ACTIVE_PAGE("Page 1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetActivePage(@Param({
        title: 'page_name',
        tip: 'page_name',
        type: Type.String
    }) pageName:AString):ABoolean
    {
        const data:ILearnExperienceActivePageData = {
            lxFunction: LearnExperienceFunction.SET_ACTIVE_PAGE,
            pageName: pageName.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_ACTIVE_SHEET()',
        tip: '',
        alias: 'LX_GET_ACTIVE_SHEET',
        example: 'LX_GET_ACTIVE_SHEET()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetActiveSheet():Promise<AString | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_SHEET
        };

        try
        {
            const responseData:ILearnExperienceActiveSheetData =
                await this._processLxGetFunc<ILearnExperienceActiveSheetData>(data);

            return AString.fromString(responseData.sheetName);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_ACTIVE_SHEET(sheet_name)',
        tip: '',
        alias: 'LX_SET_ACTIVE_SHEET',
        example: 'LX_SET_ACTIVE_SHEET("Sheet 1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetActiveSheet(@Param({
        title: 'sheet_name',
        tip: 'sheet_name',
        type: Type.String
    }) sheetName:AString):ABoolean
    {
        const data:ILearnExperienceActiveSheetData = {
            lxFunction: LearnExperienceFunction.SET_ACTIVE_SHEET,
            sheetName: sheetName.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_SELECTED_COMPONENT()',
        tip: '',
        alias: 'LX_GET_SELECTED_COMPONENT',
        example: 'LX_GET_SELECTED_COMPONENT()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetSelectedComponent():Promise<AString | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_SELECTED_COMPONENT
        };

        try
        {
            const responseData:ILearnExperienceComponentData =
                await this._processLxGetFunc<ILearnExperienceComponentData>(data);

            return AString.fromString(responseData.componentName);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_SELECTED_COMPONENT(page_name, component_name)',
        tip: '',
        alias: 'LX_SET_SELECTED_COMPONENT',
        example: 'LX_SET_SELECTED_COMPONENT("Page 1", "Box 1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetSelectedComponent(@Param({
                                      title: 'page_name',
                                      tip: 'page_name',
                                      type: Type.String
                                  }) pageName:AString,
                                  @Param({
                                      title: 'component_name',
                                      tip: 'component_name',
                                      type: Type.String
                                  }) componentName:AString):ABoolean
    {
        const data:ILearnExperienceComponentData = {
            lxFunction: LearnExperienceFunction.SET_SELECTED_COMPONENT,
            pageName: pageName.toString(),
            componentName: componentName.toString()
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_SELECTED_CELL()',
        tip: '',
        alias: 'LX_GET_SELECTED_CELL',
        example: 'LX_GET_SELECTED_CELL()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetSelectedCell():Promise<AString | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_SELECTED_CELL
        };

        try
        {
            const responseData:ILearnExperienceSelectedCellData =
                await this._processLxGetFunc<ILearnExperienceSelectedCellData>(data);

            const from:R1C1Location = new R1C1Location(
                responseData.cellRange.from.rowIndex + 1,
                false,
                responseData.cellRange.from.columnIndex + 1,
                false
            );

            if( responseData.cellRange.to )
            {
                const range:R1C1Range = new R1C1Range(
                    from,
                    new R1C1Location(
                        responseData.cellRange.to.rowIndex + 1,
                        false,
                        responseData.cellRange.to.columnIndex + 1,
                        false
                    )
                );

                return AString.fromString(range.toA1String(0, 0));
            }
            else
                return AString.fromString(from.toA1String(0, 0));
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_SELECTED_CELL(sheet_name, cell_range)',
        tip: '',
        alias: 'LX_SET_SELECTED_CELL',
        example: 'LX_SET_SELECTED_CELL("Sheet 1", A1:C)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetSelectedCell(@Param({
                                 title: 'sheet_name',
                                 tip: 'sheet_name',
                                 type: Type.String
                             }) sheetName:AString,
                             @Param({
                                 title: 'cell_range',
                                 tip: 'cell_range',
                                 type: SheetType.Any
                             }) cellRange:any):ABoolean
    {
        let lxCellRange:ILearnExperienceCellRange;

        if( SheetValueType.isInstanceOf(cellRange, SheetType.SheetLocation) )
        {
            const sheetLocation:ISheetLocation = cellRange;

            lxCellRange = {
                from: {
                    rowIndex: sheetLocation.rowIndex,
                    columnIndex: sheetLocation.columnIndex
                },
                to: null
            };
        }
        else if( SheetValueType.isInstanceOf(cellRange, SheetType.SheetRange) )
        {
            const sheetRange:ISheetRange = cellRange;

            lxCellRange = {
                from: {
                    rowIndex: sheetRange.from.rowIndex,
                    columnIndex: sheetRange.from.columnIndex
                },
                to: {
                    rowIndex: sheetRange.to.rowIndex,
                    columnIndex: sheetRange.to.columnIndex
                }
            };
        }

        const data:ILearnExperienceSelectedCellData = {
            lxFunction: LearnExperienceFunction.SET_SELECTED_CELL,
            sheetName: sheetName.toString(),
            cellRange: lxCellRange
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_CLOSE_IFRAME()',
        tip: '',
        alias: 'LX_CLOSE_IFRAME',
        example: 'LX_CLOSE_IFRAME()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxCloseIframe():ABoolean
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.CLOSE
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_CURRENT_CONTEXT()',
        tip: '',
        alias: 'LX_GET_CURRENT_CONTEXT',
        example: 'LX_GET_CURRENT_CONTEXT()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetCurrentContext():Promise<IFrame | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_CURRENT_CONTEXT
        };

        try
        {
            const responseData:ILearnExperienceCurrentContextData =
                await this._processLxGetFunc<ILearnExperienceCurrentContextData>(data);

            return Frames.fromStrings(
                ['organization_guid', 'user_guid'],
                [[responseData.organizationId, responseData.userId]]
            );
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_GET_SCREEN_BOUNDS()',
        tip: '',
        alias: 'LX_GET_SCREEN_BOUNDS',
        example: 'LX_GET_SCREEN_BOUNDS()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Frame,
            }
    })
    @bind
    public async lxGetScreenBounds():Promise<IFrame | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.GET_SCREEN_BOUNDS
        };

        try
        {
            const responseData:ILearnExperienceGetBoundsData =
                await this._processLxGetFunc<ILearnExperienceGetBoundsData>(data);

            return Frames.fromFieldsAndData(
                [
                    new FrameField('x', Type.Number),
                    new FrameField('y', Type.Number),
                    new FrameField('width', Type.Number),
                    new FrameField('height', Type.Number)
                ],
                [
                    [
                        ANumber.fromNumber(responseData.x),
                        ANumber.fromNumber(responseData.y),
                        ANumber.fromNumber(responseData.width),
                        ANumber.fromNumber(responseData.height)
                    ]
                ]
            );
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_GET_UI_PART_STATE(part_name)',
        tip: '',
        alias: 'LX_GET_UI_PART_STATE',
        example: 'LX_GET_UI_PART_STATE("outline")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetUiPartState(@Param({
        title: 'part_name',
        tip: 'part_name',
        type: Type.String
    }) partName:AString):Promise<AString | AError>
    {
        const data:ILearnExperienceUiPartStateData = {
            lxFunction: LearnExperienceFunction.GET_UI_PART_STATE,
            part: partName.value as LearnExperienceUiPart,
            partState: null,
            partData: null,
        };

        try
        {
            const responseData:ILearnExperienceUiPartStateData =
                await this._processLxGetFunc<ILearnExperienceUiPartStateData>(data);

            return AString.fromString(responseData.partState);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_UI_PART_STATE(part_name, part_state, [percent_width])',
        tip: '',
        alias: 'LX_SET_UI_PART_STATE',
        example: 'LX_SET_UI_PART_STATE("outline", "closed")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxSetUiPartState(@Param({
                                      title: 'part_name',
                                      tip: 'part_name',
                                      type: Type.String
                                  }) partName:AString,
                                  @Param({
                                      title: 'part_state',
                                      tip: 'part_state',
                                      type: Type.String
                                  }) partState:AString,
                                  @Param({
                                      title: 'percent_width',
                                      tip: 'percent_width',
                                      type: Type.Number
                                  }) percentWidth:ANumber):Promise<ABoolean | AError>
    {
        const data:ILearnExperienceUiPartStateData = {
            lxFunction: LearnExperienceFunction.SET_UI_PART_STATE,
            part: partName.value as LearnExperienceUiPart,
            partState: partState.value,
            partData: percentWidth && percentWidth.value != null ? percentWidth.value.toString() : null,
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_ADD_COMPONENT(pageName, parentName, componentType, componentName, xOffsetInParent, yOffsetInParent, width, height)',
        tip: 'Adds a new component to the page',
        alias: 'LX_ADD_COMPONENT',
        example: 'LX_ADD_COMPONENT("Page 1", "Box 1", "Box", "Box 2", 30, 50, 300, 200)',
        lazyOnly: true,
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if a component has been added to the page. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public async lxAddComponent(@Param({
                                    title: 'page_name',
                                    tip: 'Name of the page',
                                    type: Type.String
                                }) pageName:AString,
                                @Param({
                                    title: 'parent_name',
                                    tip: 'Name of the parent component',
                                    type: Type.String,
                                    optional: true
                                }) parentName:AString,
                                @Param({
                                    title: 'component_type',
                                    tip: 'Type of a new component',
                                    type: Type.String
                                }) componentType:AString,
                                @Param({
                                    title: 'component_name',
                                    tip: 'Name of a new component',
                                    type: Type.String
                                }) componentName:AString,
                                @Param({
                                    title: 'x',
                                    tip: 'X offset in parent',
                                    type: Type.Number,
                                    optional: true
                                }) x:ANumber,
                                @Param({
                                    title: 'y',
                                    tip: 'Y offset in parent',
                                    type: Type.Number,
                                    optional: true
                                }) y:ANumber,
                                @Param({
                                    title: 'width',
                                    tip: 'Width of a component',
                                    type: Type.Number,
                                    optional: true
                                }) width:ANumber,
                                @Param({
                                    title: 'height',
                                    tip: 'Height of a component',
                                    type: Type.Number,
                                    optional: true
                                }) height:ANumber):Promise<ABoolean | AError>
    {
        const data:ILearnExperienceAddComponentData = {
            lxFunction: LearnExperienceFunction.ADD_COMPONENT,
            pageName: pageName.value,
            componentName: componentName.value,
            parentName: parentName.value,
            componentType: componentType.value,
            x: x.value,
            y: y.value,
            width: width.value,
            height: height.value
        };

        try
        {
            await this._processLxGetFunc<ILearnExperienceAddComponentData>(data);

            return ABoolean.true();
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_REMOVE_COMPONENT(page_name, component_name)',
        tip: '',
        alias: 'LX_REMOVE_COMPONENT',
        example: 'LX_REMOVE_COMPONENT("Page 1", "Box 1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxRemoveComponent(@Param({
                                       title: 'page_name',
                                       tip: 'page_name',
                                       type: Type.String
                                   }) pageName:AString,
                                   @Param({
                                       title: 'component_name',
                                       tip: 'component_name',
                                       type: Type.String
                                   }) componentName:AString):Promise<ABoolean | AError>
    {
        const data:ILearnExperienceComponentData = {
            lxFunction: LearnExperienceFunction.REMOVE_COMPONENT,
            pageName: pageName.value,
            componentName: componentName.value
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_SET_BINDING(pageName, componentName, propOrEventName, sheetName, cellRef)',
        alias: 'LX_SET_BINDING',
        lazyOnly: true,
        tip: 'Sets component binding',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if binding has been set. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public lxSetBinding(@Param({
                            title: 'sheetAPI',
                            tip: 'API',
                            type: SheetType.SheetAPI
                        })api:ISheetAPI,
                        @Param({
                            title: 'pageName',
                            tip: 'Name of the page',
                            type: Type.String
                        }) pageName:AString,
                        @Param({
                            title: 'componentName',
                            tip: 'Name of the component',
                            type: Type.String
                        }) componentName:AString,
                        @Param({
                            title: 'propOrEventName',
                            tip: 'Name of the property or event',
                            type: Type.String
                        }) propOrEventName:AString,
                        @Param({
                            title: 'sheetName',
                            tip: 'Name of the sheet',
                            type: Type.String
                        }) sheetName:AString,
                        @Param({
                            title: 'cellLocation',
                            tip: 'Cell location',
                            type: SheetType.SheetLocation
                        }) cellLocation:ISheetLocation,
                        @Param({
                            title: 'pathInFrame',
                            tip: 'Path (jsonpath) in frame (e.g. column1[0].column2[0])',
                            type: Type.String,
                            optional: true
                        }) pathInFrame:AString,
                        @Param({
                            title: 'fixedDirection',
                            tip: 'Binding fixed direction (Available options: fixed, down, right. Not fixed by default.)',
                            type: Type.String,
                            optional: true
                        }) fixedDirection:AString,
                        @Param({
                            title: 'useFormat',
                            tip: 'Whether use formatting or not, boolean (Available options: true, false)',
                            type: Type.Boolean,
                            optional: true
                        }) useFormat:ABoolean):ABoolean | AError
    {
        const result:boolean | Error = commonControllers.bindings.setBinding(
            pageName.value,
            componentName.value,
            propOrEventName.value,
            sheetName.value,
            cellLocation,
            pathInFrame ? pathInFrame.value : null,
            fixedDirection ? fixedDirection.value : null,
            useFormat ? useFormat.value : null,
            null,
            null
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'LX_SET_COMPONENT_POSITION(page_name, component_name, new_x, new_y)',
        tip: '',
        alias: 'LX_SET_COMPONENT_POSITION',
        example: 'LX_SET_COMPONENT_POSITION("Page 1", "Box 1", 20, 30)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxSetComponentPosition(@Param({
                                            title: 'page_name',
                                            tip: 'page_name',
                                            type: Type.String
                                        }) pageName:AString,
                                        @Param({
                                            title: 'component_name',
                                            tip: 'component_name',
                                            type: Type.String
                                        }) componentName:AString,
                                        @Param({
                                            title: 'new_x',
                                            tip: 'new_x',
                                            type: Type.Number
                                        }) newX:ANumber,
                                        @Param({
                                            title: 'new_y',
                                            tip: 'new_y',
                                            type: Type.Number
                                        }) newY:ANumber):Promise<ABoolean | AError>
    {
        const data:ILearnExperienceSetComponentPositionData = {
            lxFunction: LearnExperienceFunction.SET_COMPONENT_POSITION,
            pageName: pageName.value,
            componentName: componentName.value,
            x: newX.value,
            y: newY.value
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_MOVE_COMPONENT(page_name, component_name, delta_x, delta_y)',
        tip: '',
        alias: 'LX_MOVE_COMPONENT',
        example: 'LX_MOVE_COMPONENT("Page 1", "Box 1", 20, 30)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxMoveComponent(@Param({
                                     title: 'page_name',
                                     tip: 'page_name',
                                     type: Type.String
                                 }) pageName:AString,
                                 @Param({
                                     title: 'component_name',
                                     tip: 'component_name',
                                     type: Type.String
                                 }) componentName:AString,
                                 @Param({
                                     title: 'delta_x',
                                     tip: 'delta_x',
                                     type: Type.Number
                                 }) deltaX:ANumber,
                                 @Param({
                                     title: 'delta_y',
                                     tip: 'delta_y',
                                     type: Type.Number
                                 }) deltaY:ANumber):Promise<ABoolean | AError>
    {
        const data:ILearnExperienceMoveComponentData = {
            lxFunction: LearnExperienceFunction.MOVE_COMPONENT,
            pageName: pageName.value,
            componentName: componentName.value,
            deltaX: deltaX.value,
            deltaY: deltaY.value
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_CELL_STATE(sheet_name, cell_ref)',
        tip: '',
        alias: 'LX_GET_CELL_STATE',
        example: 'LX_GET_CELL_STATE("Sheet 1", B2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetCellState(@Param({
                                    title: 'sheet_name',
                                    tip: 'sheet_name',
                                    type: Type.String
                                }) sheetName:AString,
                                @Param({
                                    title: 'cell_ref',
                                    tip: 'cell_ref',
                                    type: SheetType.SheetLocation
                                }) cellRef:ISheetLocation):Promise<IFrame | AError>
    {
        let lxCellLocation:ILearnExperienceCellLocation = {
            rowIndex: cellRef.rowIndex,
            columnIndex: cellRef.columnIndex
        };

        const data:ILearnExperienceCellStateData = {
            lxFunction: LearnExperienceFunction.LX_GET_CELL_STATE,
            sheetName: sheetName.value,
            cellRef: lxCellLocation,
            cellState: null,
            isLazy: null,
            isAccumulating: null,
            isRealtime: null,
            cellFormula: null,
            cellValue: null
        };

        try
        {
            const responseData:ILearnExperienceCellStateData =
                await this._processLxGetFunc<ILearnExperienceCellStateData>(data);

            const value:IValue = Type.deserializeIValue(responseData.cellValue);

            return Frames.fromFieldsAndData(
                [
                    new FrameField('cell_state', Type.String),
                    new FrameField('is_lazy', Type.Boolean),
                    new FrameField('is_accumulating', Type.Boolean),
                    new FrameField('is_realtime', Type.Boolean),
                    new FrameField('formula', Type.String),
                    new FrameField('value', value.getType())
                ],
                [
                    [
                        AString.fromString(responseData.cellState),
                        ABoolean.fromBoolean(responseData.isLazy),
                        ABoolean.fromBoolean(responseData.isAccumulating),
                        ABoolean.fromBoolean(responseData.isRealtime),
                        responseData.cellFormula
                            ? AString.fromString(responseData.cellFormula)
                            : AEmpty.Value,
                        value
                    ]
                ]
            );
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_CELL_STATE(sheet_name, cell_ref, cell_state, [cell_formula])',
        tip: '',
        alias: 'LX_SET_CELL_STATE',
        example: 'LX_SET_CELL_STATE("Sheet 1", A1, "active.editing", "=A2+1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetCellState(@Param({
                              title: 'sheet_name',
                              tip: 'sheet_name',
                              type: Type.String
                          }) sheetName:AString,
                          @Param({
                              title: 'cell_ref',
                              tip: 'cell_ref',
                              type: SheetType.SheetLocation
                          }) cellRef:ISheetLocation,
                          @Param({
                              title: 'cell_state',
                              tip: 'cell_state',
                              type: Type.String
                          }) cellState:AString,
                          @Param({
                              title: 'cell_formula',
                              tip: 'cell_formula',
                              type: Type.String
                          }) cellFormula:AString):ABoolean
    {
        let lxCellLocation:ILearnExperienceCellLocation = {
            rowIndex: cellRef.rowIndex,
            columnIndex: cellRef.columnIndex
        };

        const data:ILearnExperienceCellStateData = {
            lxFunction: LearnExperienceFunction.LX_SET_CELL_STATE,
            sheetName: sheetName.value,
            cellRef: lxCellLocation,
            cellState: cellState.value as LearnExperienceCellState,
            isLazy: null,
            isAccumulating: null,
            isRealtime: null,
            cellFormula: cellFormula ? cellFormula.value : null,
            cellValue: null
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_FRAME_DOCK()',
        tip: '',
        alias: 'LX_GET_FRAME_DOCK',
        example: 'LX_GET_FRAME_DOCK()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.String,
            }
    })
    @bind
    public async lxGetFrameDock():Promise<AString | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.LX_GET_FRAME_DOCK
        };

        try
        {
            const responseData:ILearnExperienceFrameDockData =
                await this._processLxGetFunc<ILearnExperienceFrameDockData>(data);

            return AString.fromString(responseData.dockSide);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_FRAME_DOCK(side, [size])',
        tip: '',
        alias: 'LX_SET_FRAME_DOCK',
        example: 'LX_SET_FRAME_DOCK("floating", "50%")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetFrameDock(@Param({
                              title: 'side',
                              tip: 'side',
                              type: Type.String
                          }) side:AString,
                          @Param({
                              title: 'size',
                              tip: 'size',
                              optional: true,
                              type: Type.String
                          }) size:AString):ABoolean
    {
        const data:ILearnExperienceFrameDockData = {
            lxFunction: LearnExperienceFunction.LX_SET_FRAME_DOCK,
            dockSide: side.value as LearnExperienceFrameDockSide,
            dockSize: size && size.value
        };

        return this._processLxFunc(data);
    }

    @Function({
        title: 'LX_GET_FRAME_FULL_OVERLAY()',
        tip: '',
        alias: 'LX_GET_FRAME_FULL_OVERLAY',
        example: 'LX_GET_FRAME_FULL_OVERLAY()',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async lxGetFrameFullOverlay():Promise<ABoolean | AError>
    {
        const data:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.LX_GET_FRAME_FULL_OVERLAY
        };

        try
        {
            const responseData:ILearnExperienceFrameFullOverlayData =
                await this._processLxGetFunc<ILearnExperienceFrameFullOverlayData>(data);

            return ABoolean.fromBoolean(responseData.isOverlay);
        }
        catch( error )
        {
            return AError.fromString(error.message);
        }
    }

    @Function({
        title: 'LX_SET_FRAME_FULL_OVERLAY(is_overlay)',
        tip: '',
        alias: 'LX_SET_FRAME_FULL_OVERLAY',
        example: 'LX_SET_FRAME_FULL_OVERLAY(true)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public lxSetFrameFullOverlay(@Param({
        title: 'is_overlay',
        tip: 'is_overlay',
        type: Type.Boolean
    }) isOverlay:ABoolean):ABoolean
    {
        const data:ILearnExperienceFrameFullOverlayData = {
            lxFunction: LearnExperienceFunction.LX_SET_FRAME_FULL_OVERLAY,
            isOverlay: isOverlay.value
        };

        return this._processLxFunc(data);
    }
}
